import { DocumentTextIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';

import { BaseDetailDrawer } from '@/components/postgrid/common/BaseDetailDrawer';
import { MailItemDetailsContent } from '@/components/postgrid/common/MailItemDetailsContent';
import type { ReadDetailedPostgridLetter } from '@/services/api/generated/postgridService';
import {
  useGetLetterDetailedQuery,
  usePostgridEventsListQuery,
  usePostgridLettersCancelMutation,
} from '@/services/api/postgridService';

interface LetterDetailsProps {
  letterId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LetterDetails: React.FC<LetterDetailsProps> = ({
  letterId,
  open,
  onOpenChange,
}) => {
  const {
    data: letter,
    isLoading,
    error,
  } = useGetLetterDetailedQuery(letterId, { skip: !letterId });

  const [cancelLetter, { isLoading: isCancelling }] =
    usePostgridLettersCancelMutation();

  const { data: events = [] } = usePostgridEventsListQuery(
    { objectId: letterId },
    { skip: !letterId },
  );

  const handleCancel = async () => {
    if (!letter || isCancelling) return;

    try {
      await cancelLetter({ id: letter.id as string }).unwrap();
      toast.success('Letter cancelled');
      onOpenChange(false);
    } catch {
      toast.error('Failed to cancel letter');
    }
  };

  const renderIcon = () => (
    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
      <DocumentTextIcon className="w-5 h-5 text-white" />
    </div>
  );

  const renderTitle = (item: ReadDetailedPostgridLetter) =>
    item.subject || 'Letter';

  const renderDescription = (item: ReadDetailedPostgridLetter) => {
    const recipient =
      item.to_contact?.company_name ||
      [item.to_contact?.first_name, item.to_contact?.last_name]
        .filter(Boolean)
        .join(' ') ||
      'Recipient';
    return `To: ${recipient}`;
  };

  const renderContent = (item: ReadDetailedPostgridLetter) => (
    <MailItemDetailsContent
      entityType="letter"
      status={item.status}
      live={item.live}
      mailingClass={item.mailing_class}
      trackingNumber={item.tracking_number}
      carrierTracking={item.carrier_tracking}
      toContact={item.to_contact}
      fromContact={item.from_contact}
      dateSent={item.date_sent}
      dateDelivered={item.date_delivered}
      expectedDeliveryDate={item.expected_delivery_date}
      finalPdfUrl={item.final_pdf_url}
      attachments={item.attachments}
      matterId={item.matter_id}
      returnEnvelopeId={item.return_envelope_id}
      color={item.color}
      doubleSided={item.double_sided}
      postgridId={item.postgrid_id}
      internalId={item.id as string}
      events={events}
      onCancel={handleCancel}
      isCancelling={isCancelling}
      letter={{
        subject: item.subject,
        content: item.content,
        letterSize: item.letter_size,
      }}
    />
  );

  return (
    <BaseDetailDrawer<ReadDetailedPostgridLetter>
      data={letter}
      isLoading={isLoading}
      error={error as Error | null}
      open={open}
      onClose={() => onOpenChange(false)}
      renderIcon={renderIcon}
      renderTitle={renderTitle}
      renderDescription={renderDescription}
      renderContent={renderContent}
      className=""
      loadingMessage="Loading letter details..."
      errorMessage="Failed to load letter details"
      canEdit={() => false}
    />
  );
};
